import {WriteButton} from '../../common/buttons/WriteActions';
import {useDelayedNameError, nameErrorHelperSx} from '../../../hooks/useDelayedNameError';
import {validateCatalogName} from '../../../common/catalogNameValidation';
import {mobileDialogSx} from '../../common/dialogs/mobileDialogSx';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar, Stack,
    TextField
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {itemAPI} from "../../../services/ItemServices";
import {UNIT_OF_WEIGHT} from "@mybake/ui/common/dictionary";
import {useEffect, useState} from "react";
import {CreateItemRequest} from "../../../models/Items/IItem";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';

type Props = {
    handleDialog: () => void;
    open: boolean;
};

export function CreateItem(
    {
        handleDialog,
        open,
    }: Props
) {
    const {t} = useAppTranslation();
    const {
        data: itemNames,
        error: getAllItemsErrors,
        isFetching: namesFetching,
    } = itemAPI.useGetAvailableItemNamesQuery(undefined, {
        skip: !open, refetchOnMountOrArgChange: true
    });
    const [createItem, {error: saveItemError}] = itemAPI.useCreateItemMutation();
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    const [newItem, setNewItem] = useState<Omit<CreateItemRequest, 'unitOfWeight'> & {unitOfWeight: CreateItemRequest['unitOfWeight'] | ''}>({
        name: '',
        unitOfWeight: '',
    });

    const nameValidation = validateCatalogName(newItem.name, itemNames);
    const nameError = useDelayedNameError(newItem.name, nameValidation === 'too_short' ? (newItem.name ? t('common:name_min_length') : '') : nameValidation === 'duplicate' ? t('items:ingredient_name_exists') : '');
    const disableSaveButton = !!nameValidation || !itemNames || namesFetching || !!getAllItemsErrors || !newItem.unitOfWeight;

    useEffect(() => {
        if (!getAllItemsErrors || getApiErrorKind(getAllItemsErrors) !== 'server') return;
        setServerToast(getApiErrorMessage(getAllItemsErrors, t));
        setTraceId(getApiTraceId(getAllItemsErrors));
    }, [getAllItemsErrors, t]);

    const closeDialog = () => {
        setNewItem(() => ({
            name: '',
            unitOfWeight: '',
        }))
        handleDialog();
    }

    const handleSaveItem = async () => {
        if (disableSaveButton || disableButton || !newItem.unitOfWeight) return;
        setDisableButton(true);
        try {
            await createItem({name: newItem.name.trim(), unitOfWeight: newItem.unitOfWeight})
                .unwrap();
            closeDialog();
            setDisableButton(false);
        } catch (error) {
            setDisableButton(false);
            if (getApiErrorKind(error) === 'server') {
                setServerToast(getApiErrorMessage(error, t));
                setTraceId(getApiTraceId(error));
            }
        }
    }

    const handleNewItemName = (e: any) => {

        setNewItem(prev => ({
            ...prev,
            name: e.target.value,
        }))
    }

    return (
        <Dialog sx={mobileDialogSx} open={open} onClose={closeDialog} fullWidth maxWidth="xs" slotProps={{
            backdrop: {
                sx: {
                    backdropFilter: 'blur(6px)',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                },
            },
        }}>
            <DialogTitle>{t('items:create_new_item')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <DialogContentText>{t('items:create_new_item_dialog_content')}</DialogContentText>

                {getAllItemsErrors && getApiErrorKind(getAllItemsErrors) !== 'server' && <Alert severity="error">{getApiErrorMessage(getAllItemsErrors, t)}</Alert>}

                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label={t('items:item_name')}
                    type="text"
                    required
                    fullWidth
                    value={newItem.name}
                    onChange={handleNewItemName}
                    error={!!nameError || Boolean(getApiFieldErrors(saveItemError, t).name)}
                    helperText={nameError || getApiFieldErrors(saveItemError, t).name || ' '} FormHelperTextProps={{sx: nameErrorHelperSx}}
                />

                <FormControl fullWidth required>
                    <InputLabel id="create-ingredient-unit-label">
                        {t('items:unit_of_weight')}
                    </InputLabel>

                    <Select
                        labelId="create-ingredient-unit-label"
                        label={t('items:unit_of_weight')}
                        value={newItem.unitOfWeight}
                        onChange={(e) =>
                            setNewItem(prev => ({
                                ...prev,
                                unitOfWeight: e.target.value as UNIT_OF_WEIGHT,
                            }))
                        }
                    >
                        {Object.values(UNIT_OF_WEIGHT).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                                {unit}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {saveItemError && getApiErrorKind(saveItemError) !== 'server' && !getApiFieldErrors(saveItemError, t).name && <Alert severity="error">{getApiErrorMessage(saveItemError, t)}</Alert>}

                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                <Button onClick={closeDialog} disabled={disableButton}>{t('common:cancel')}</Button>
                <WriteButton variant="contained" onClick={handleSaveItem}
                        endIcon={<SendIcon/>}
                        disabled={disableSaveButton || disableButton}>
                    {t('common:create')}
                </WriteButton>
            </DialogActions>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Dialog>
    );
}
